/**
 * 认证相关工具函数
 * 提供登录、登出等认证相关的辅助功能
 */

import { NavigateFunction } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { UseToastOptions } from '@chakra-ui/react';

/**
 * 核心登出逻辑 - 所有登出操作的统一入口
 * 只处理Supabase登出和本地存储清理，不涉及UI操作
 *
 * @returns 登出是否成功
 */
export const performSignOut = async (): Promise<boolean> => {
  console.log('[AuthUtils] 执行核心登出逻辑');
  let success = true;

  try {
    // 1. 尝试Supabase登出
    try {
      await supabase.auth.signOut();
      console.log('[AuthUtils] Supabase 登出成功');
    } catch (error) {
      console.warn('[AuthUtils] Supabase 登出失败，继续执行本地清理:', error);
      success = false;
    }

    // 2. 清理本地存储
    try {
      // 清除 Supabase 相关存储
      localStorage.removeItem('supabase.auth.token');
      localStorage.removeItem('supabase.auth.expires_at');
      localStorage.removeItem('supabase.auth.refresh_token');

      // 清除所有本地存储和会话存储
      localStorage.clear();
      sessionStorage.clear();

      console.log('[AuthUtils] 本地存储清理完成');
    } catch (e) {
      console.error('[AuthUtils] 清理本地存储时出错:', e);
      success = false;
    }

    return success;
  } catch (error) {
    console.error('[AuthUtils] 核心登出逻辑发生错误:', error);
    return false;
  }
};

/**
 * 强制登出函数 - 即使在离线状态下也能工作
 * 清除本地存储并重定向到登录页面
 * 
 * @param navigate - React Router 的 navigate 函数
 * @param toast - Chakra UI 的 toast 函数
 * @param successMessage - 成功消息
 * @param errorMessage - 错误消息
 * @returns 登出是否成功
 */
export const forceSignOut = async (
  navigate: NavigateFunction,
  toast: (options: UseToastOptions) => void,
  successMessage: string = '退出成功',
  errorMessage: string = '退出失败'
): Promise<boolean> => {
  console.log('[AuthUtils] 执行强制登出（带UI反馈）');

  try {
    // 使用核心登出逻辑
    const success = await performSignOut();

    // 显示反馈消息
    toast({
      title: successMessage,
      status: success ? 'success' : 'warning',
      description: success ? undefined : '无法连接到服务器，但已清除本地登录状态',
      duration: 3000,
      isClosable: true,
    });

    // 导航到登录页面
    navigate('/login');
    return true;
  } catch (error) {
    console.error('[AuthUtils] 强制登出过程中发生错误:', error);

    toast({
      title: errorMessage,
      description: error instanceof Error ? error.message : '未知错误',
      status: 'error',
      duration: 3000,
      isClosable: true,
    });

    return false;
  }
};
